import { useSuggestionsShallowSelector } from 'components/WorkspaceEditor/SuggestionStore'
import { withDebounce } from 'components/WorkspaceEditor/utils/workspaceEditorUtils'
import { useCallback, useEffect, useRef } from 'react'
import { useTrackChangesQuery } from '../api'
import { UseDebouncedFetchProps } from '../types/types'

/**
 * Custom hook to handle debounced fetching of track changes data.
 * It uses a provided fetch function or defaults to fetching track changes data.
 * The fetching is debounced to avoid excessive calls.
 *
 * @param {UseDebouncedFetchProps} props - Optional properties for the hook.
 * @returns {Object} - An object containing the debounced fetch function.
 */
export const useDebouncedFetch = (props?: UseDebouncedFetchProps) => {
    const { fetchFunction, delay = 300 } = props || {}
    const isMountedRef = useRef(true)
    const debouncedFetchAndProcessRef = useRef<any>(null)
    const {
        setSuggestionCardFilter,
        setIsLoadingCards,
    } = useSuggestionsShallowSelector((state) => ({
        setIsLoadingCards: state.setIsLoadingCards,
        setSuggestionCardFilter: state.setSuggestionCardFilter,
    }))
    const { fetchTrackChangesData } = useTrackChangesQuery()
    const fetchFn = fetchFunction || fetchTrackChangesData

    const fetchAndProcess = useCallback(async () => {
        if (!isMountedRef.current) return
        try {
            await fetchFn()
        } catch (error) {
            console.error('Error fetching data:', error)
            if (isMountedRef.current && setIsLoadingCards) {
                setIsLoadingCards(false)
            }
        }
    }, [fetchFn, setSuggestionCardFilter, setIsLoadingCards])

    const debouncedFetchAndProcess = useCallback(
        withDebounce<() => Promise<void>>(async () => {
            await fetchAndProcess()
        }, delay),
        [fetchAndProcess, delay]
    )

    debouncedFetchAndProcessRef.current = debouncedFetchAndProcess

    useEffect(() => {
        isMountedRef.current = true

        return () => {
            isMountedRef.current = false

            if (
                debouncedFetchAndProcessRef.current &&
                typeof debouncedFetchAndProcessRef.current.cancel === 'function'
            ) {
                debouncedFetchAndProcessRef.current.cancel()
            }
        }
    }, [])

    return {
        debouncedFetchAndProcess,
    }
}
